import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AdminCalendar.module.css';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'admissionsAdminToken';
const BOARD_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

function startOfWeek(date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  next.setHours(12, 0, 0, 0);
  return next;
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function ymdInZone(date, timezone) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function formatWhen(iso, timezone) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatTime(iso, timezone) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

function eventOnDay(event, ymd, timezone) {
  return ymdInZone(new Date(event.startIso), timezone) === ymd;
}

function hourInZone(iso, timezone) {
  const hour = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || 'America/New_York',
    hour: 'numeric',
    hourCycle: 'h23',
  })
    .formatToParts(new Date(iso))
    .find((part) => part.type === 'hour')?.value;
  const value = Number(hour);
  return Number.isFinite(value) ? value : 0;
}

function hourLabel(hour) {
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12} ${suffix}`;
}

function zonedStartIso(ymd, hour, timezone) {
  const [year, month, day] = String(ymd || '')
    .split('-')
    .map(Number);
  if (!year || !month || !day || !Number.isFinite(hour)) return '';
  const pad = (value) => String(value).padStart(2, '0');
  const wanted = `${ymd}T${pad(hour)}:00:00`;
  const wallStamp = (date) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'America/New_York',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value || '00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
  };
  let instant = Date.UTC(year, month - 1, day, hour, 0, 0);
  for (let step = 0; step < 4; step += 1) {
    const wall = wallStamp(new Date(instant));
    const delta = Date.parse(`${wanted}Z`) - Date.parse(`${wall}Z`);
    if (delta === 0) break;
    instant += delta;
  }
  return new Date(instant).toISOString();
}

function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return `${parts[0]?.[0] || 'A'}${parts[1]?.[0] || ''}`.toUpperCase();
}

function meetingKind(event) {
  if (event.kind !== 'booking') return event.kind;
  return Number(event.durationMinutes) <= 20 ? 'triage' : 'admissions';
}

function kindLabel(event) {
  const kind = meetingKind(event);
  if (kind === 'triage') return '15m triage';
  if (kind === 'admissions') return '45m admissions';
  if (kind === 'hold') return 'Hold';
  return 'Busy';
}

function eventMatches(event, needle) {
  if (!needle) return true;
  return [event.title, event.guest?.name, event.guest?.email, event.guest?.phone, event.provider?.name]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
}

const NAV = [
  { id: 'board', label: 'Board', icon: 'calendar_view_week' },
  { id: 'queue', label: 'Queue', icon: 'list_alt' },
  { id: 'team', label: 'Team', icon: 'group' },
  { id: 'holds', label: 'Holds', icon: 'event_busy' },
];

export default function AdminCalendar() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [focusYmd, setFocusYmd] = useState(() => toYmd(new Date()));
  const [view, setView] = useState('board');
  const [calendars, setCalendars] = useState([]);
  const [timezone, setTimezone] = useState('America/New_York');
  const [durationMinutes, setDurationMinutes] = useState(45);
  const [bufferMinutes, setBufferMinutes] = useState(15);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [moveDate, setMoveDate] = useState(toYmd(new Date()));
  const [moveProviderId, setMoveProviderId] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [hold, setHold] = useState({
    providerId: '',
    date: toYmd(new Date()),
    start: '13:00',
    end: '14:00',
    note: '',
  });
  const [dragId, setDragId] = useState('');
  const [dragLabel, setDragLabel] = useState('');
  const [dropKey, setDropKey] = useState('');
  const dragRef = useRef(null);
  const skipClickRef = useRef(false);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const loadCalendar = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const from = toYmd(days[0]);
      const to = toYmd(days[6]);
      const res = await fetch(`${API_URL}/api/admin/calendar?from=${from}&to=${to}`, {
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (res.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken('');
        }
        setError(data.message || 'Could not load calendars.');
        return;
      }
      setCalendars(data.calendars || []);
      setTimezone(data.timezone || 'America/New_York');
      setDurationMinutes(data.durationMinutes || 45);
      setBufferMinutes(data.bufferMinutes || 15);
      if (!hold.providerId && data.calendars?.[0]?.provider?.id) {
        setHold((current) => ({ ...current, providerId: data.calendars[0].provider.id }));
      }
    } catch (err) {
      setError('Network error loading calendars.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, days, hold.providerId, token]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const todayYmd = ymdInZone(new Date(), timezone);
  useEffect(() => {
    const ids = days.map(toYmd);
    if (!ids.includes(focusYmd)) setFocusYmd(ids.includes(todayYmd) ? todayYmd : ids[0]);
  }, [days, focusYmd, todayYmd]);

  const bookings = useMemo(
    () =>
      calendars.flatMap((calendar) =>
        (calendar.events || [])
          .filter((event) => event.kind === 'booking')
          .map((event) => ({ ...event, provider: calendar.provider }))
      ),
    [calendars]
  );

  const holds = useMemo(
    () =>
      calendars.flatMap((calendar) =>
        (calendar.events || [])
          .filter((event) => event.kind === 'hold')
          .map((event) => ({ ...event, provider: calendar.provider }))
      ),
    [calendars]
  );

  const visibleCalendars = useMemo(() => {
    if (advisorFilter === 'all') return calendars;
    return calendars.filter((calendar) => calendar.provider.id === advisorFilter);
  }, [advisorFilter, calendars]);

  const needle = query.trim().toLowerCase();
  const filteredBookings = useMemo(() => {
    return bookings
      .filter((event) => {
        if (advisorFilter !== 'all' && event.provider.id !== advisorFilter) return false;
        return eventMatches(event, needle);
      })
      .slice()
      .sort((a, b) => Date.parse(a.startIso) - Date.parse(b.startIso));
  }, [advisorFilter, bookings, needle]);

  const score = useMemo(() => {
    const now = Date.now();
    return {
      today: bookings.filter((event) => eventOnDay(event, todayYmd, timezone)).length,
      upcoming: bookings.filter((event) => Date.parse(event.startIso) >= now).length,
      triage: bookings.filter((event) => meetingKind(event) === 'triage').length,
      admissions: bookings.filter((event) => meetingKind(event) === 'admissions').length,
    };
  }, [bookings, timezone, todayYmd]);

  const focusDay = days.find((day) => toYmd(day) === focusYmd) || days[0];
  const focusTitle = focusDay
    ? focusDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : 'Board';

  async function handleLogin(event) {
    event.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not sign in.');
        return;
      }
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword('');
    } catch (err) {
      setError('Network error signing in.');
    }
  }

  function signOut() {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setSelected(null);
  }

  function openBooking(event, provider) {
    const next = { ...event, provider: provider || event.provider };
    setSelected(next);
    setMoveProviderId(next.provider.id);
    setMoveDate(ymdInZone(new Date(event.startIso), timezone));
    setNotice('');
    setError('');
  }

  const loadSlots = useCallback(async () => {
    if (!token || !moveProviderId || !moveDate || !selected) return;
    setSlotsLoading(true);
    try {
      const duration = selected.durationMinutes || durationMinutes;
      const res = await fetch(
        `${API_URL}/api/admin/slots?providerId=${encodeURIComponent(moveProviderId)}&date=${moveDate}&durationMinutes=${duration}&excludeEventId=${encodeURIComponent(selected.id)}`,
        { headers: authHeaders }
      );
      const data = await res.json();
      setSlots(res.ok && data.success ? data.slots || [] : []);
    } catch (err) {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [authHeaders, durationMinutes, moveDate, moveProviderId, selected, token]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  async function moveCall(booking, toProviderId, startIso) {
    if (!booking?.id || !toProviderId || !startIso || working) return;
    const sameAdvisor = booking.provider?.id === toProviderId;
    const sameHour = hourInZone(booking.startIso, timezone) === hourInZone(startIso, timezone);
    const sameDay = eventOnDay(booking, ymdInZone(new Date(startIso), timezone), timezone);
    if (sameAdvisor && sameHour && sameDay) return;
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/move`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          fromProviderId: booking.provider.id,
          toProviderId,
          eventId: booking.id,
          startIso,
          durationMinutes: booking.durationMinutes,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not move that call.');
        return;
      }
      setNotice(data.message || 'Call moved.');
      if (selected?.id === booking.id) setSelected(null);
      await loadCalendar();
    } catch (err) {
      setError('Could not move that call.');
    } finally {
      setWorking(false);
    }
  }

  async function runMove(startIso) {
    if (!selected) return;
    await moveCall(selected, moveProviderId, startIso);
  }

  function beginDrag(event, provider, nativeEvent) {
    if (event.kind !== 'booking' || working) return;
    skipClickRef.current = true;
    dragRef.current = { ...event, provider };
    setDragId(event.id);
    setDragLabel(event.guest?.name || event.title || 'that call');
    setDropKey('');
    nativeEvent.dataTransfer.effectAllowed = 'move';
    nativeEvent.dataTransfer.setData('text/plain', event.id);
  }

  function endDrag() {
    dragRef.current = null;
    setDragId('');
    setDragLabel('');
    setDropKey('');
  }

  function allowDrop(key, nativeEvent) {
    if (!dragRef.current) return;
    nativeEvent.preventDefault();
    nativeEvent.dataTransfer.dropEffect = 'move';
    if (dropKey !== key) setDropKey(key);
  }

  async function dropOnCell(providerId, hour, nativeEvent) {
    nativeEvent.preventDefault();
    const booking = dragRef.current;
    endDrag();
    if (!booking) return;
    const startIso = zonedStartIso(focusYmd, hour, timezone);
    await moveCall(booking, providerId, startIso);
  }

  async function dropOnDay(ymd, nativeEvent) {
    nativeEvent.preventDefault();
    const booking = dragRef.current;
    endDrag();
    if (!booking) return;
    const startIso = zonedStartIso(ymd, hourInZone(booking.startIso, timezone), timezone);
    await moveCall(booking, booking.provider.id, startIso);
  }

  function handleEventClick(event, provider) {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    if (event.kind === 'booking') openBooking(event, provider);
  }

  async function cancelSelected() {
    if (!selected) return;
    if (!window.confirm(`Cancel ${selected.guest?.name || 'this'} call? Reminders will be removed.`)) return;
    setWorking(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/cancel`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ providerId: selected.provider.id, eventId: selected.id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not cancel that call.');
        return;
      }
      setNotice(data.message || 'Call canceled.');
      setSelected(null);
      await loadCalendar();
    } catch (err) {
      setError('Could not cancel that call.');
    } finally {
      setWorking(false);
    }
  }

  async function protectTime(event) {
    event.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/holds`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(hold),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not protect that time.');
        return;
      }
      setNotice('Time protected on the public calendar.');
      await loadCalendar();
    } catch (err) {
      setError('Could not protect that time.');
    }
  }

  async function protectDay(providerId, date) {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/holds/day`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ providerId, date }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not close that day.');
        return;
      }
      setNotice('That day is closed on the public calendar.');
      await loadCalendar();
    } catch (err) {
      setError('Could not close that day.');
    }
  }

  async function removeHold(providerId, eventId) {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/holds/delete`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ providerId, eventId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not release that hold.');
        return;
      }
      await loadCalendar();
    } catch (err) {
      setError('Could not release that hold.');
    }
  }

  async function setAdvisorVisible(providerId, visible) {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/visibility`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ providerId, visible }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not update that advisor.');
        return;
      }
      await loadCalendar();
    } catch (err) {
      setError('Could not update that advisor.');
    }
  }

  function eventsFor(calendar, hour) {
    return (calendar.events || []).filter((event) => {
      if (!eventOnDay(event, focusYmd, timezone)) return false;
      if (hourInZone(event.startIso, timezone) !== hour) return false;
      return eventMatches({ ...event, provider: calendar.provider }, needle);
    });
  }

  if (!token) {
    return (
      <div className={styles.login}>
        <section className={styles.loginStory}>
          <p className={styles.brandMark}>Admissions</p>
          <h1>One board for every SSA calendar.</h1>
          <p>Reschedule a call, hand it to another advisor, or close overflow without opening Google Calendar.</p>
          <ul>
            <li>Day view, Eastern time</li>
            <li>Triage and 45-minute calls stay separate</li>
            <li>Moving a call keeps one reminder chain</li>
          </ul>
        </section>
        <form className={styles.loginForm} onSubmit={handleLogin}>
          <p className={styles.kicker}>Command center</p>
          <h2>Sign in</h2>
          {error ? <p className={styles.error}>{error}</p> : null}
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>
          <button type="submit">Open the board</button>
        </form>
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <aside className={styles.rail}>
        <div className={styles.brand}>
          <span>A</span>
          <div>
            <strong>Admissions</strong>
            <small>Command center</small>
          </div>
        </div>
        <nav>
          {NAV.map((item) => (
            <button
              key={item.id}
              type="button"
              className={view === item.id ? styles.navOn : styles.nav}
              onClick={() => setView(item.id)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              {item.label}
              {item.id === 'queue' ? <em>{filteredBookings.length}</em> : null}
            </button>
          ))}
        </nav>
        <div className={styles.railFoot}>
          <p>Eastern time · {bufferMinutes}m buffer</p>
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        <header className={styles.head}>
          <div>
            <p className={styles.kicker}>{view === 'board' ? 'This week' : 'Admissions'}</p>
            <h1>{view === 'board' ? focusTitle : NAV.find((item) => item.id === view)?.label}</h1>
          </div>
          <div className={styles.headTools}>
            {view === 'board' || view === 'queue' ? (
              <input
                className={styles.search}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search guest, email, or phone"
              />
            ) : null}
            <div className={styles.weekNav}>
              <button type="button" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setWeekStart(startOfWeek(new Date()));
                  setFocusYmd(ymdInZone(new Date(), timezone));
                }}
              >
                Today
              </button>
              <button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
        </header>

        {error ? <p className={styles.error}>{error}</p> : null}
        {notice ? <p className={styles.notice}>{notice}</p> : null}

        {view === 'board' ? (
          <>
            <section className={styles.metrics}>
              <article>
                <span>Today</span>
                <strong>{score.today}</strong>
              </article>
              <article>
                <span>Still ahead</span>
                <strong>{score.upcoming}</strong>
              </article>
              <article>
                <span>Triage</span>
                <strong>{score.triage}</strong>
              </article>
              <article>
                <span>Admissions</span>
                <strong>{score.admissions}</strong>
              </article>
            </section>

            <div className={styles.dayRail}>
              {days.map((day) => {
                const ymd = toYmd(day);
                const count = bookings.filter(
                  (event) =>
                    eventOnDay(event, ymd, timezone) &&
                    (advisorFilter === 'all' || event.provider.id === advisorFilter)
                ).length;
                return (
                  <button
                    key={ymd}
                    type="button"
                    className={`${ymd === focusYmd ? styles.dayOn : styles.day} ${
                      dropKey === `day-${ymd}` ? styles.dayDrop : ''
                    }`}
                    onClick={() => setFocusYmd(ymd)}
                    onDragOver={(nativeEvent) => allowDrop(`day-${ymd}`, nativeEvent)}
                    onDrop={(nativeEvent) => dropOnDay(ymd, nativeEvent)}
                  >
                    <small>{day.toLocaleDateString('en-US', { weekday: 'short' })}</small>
                    <strong>{day.getDate()}</strong>
                    <em>{ymd === todayYmd ? 'Today' : count === 1 ? '1 call' : `${count} calls`}</em>
                  </button>
                );
              })}
            </div>

            <div className={styles.filters}>
              <button
                type="button"
                className={advisorFilter === 'all' ? styles.filterOn : styles.filter}
                onClick={() => setAdvisorFilter('all')}
              >
                All advisors
              </button>
              {calendars.map((calendar) => (
                <button
                  key={calendar.provider.id}
                  type="button"
                  className={advisorFilter === calendar.provider.id ? styles.filterOn : styles.filter}
                  onClick={() => setAdvisorFilter(calendar.provider.id)}
                >
                  {calendar.provider.name.split(' ')[0]}
                </button>
              ))}
            </div>

            <p className={styles.dragHint}>
              {dragId
                ? `Drop ${dragLabel} on an advisor hour to move it.`
                : 'Drag a call onto another advisor or hour. Drop it on a day to keep the same time.'}
            </p>
            <section className={`${styles.scheduleWrap} ${dragId ? styles.draggingBoard : ''}`}>
              {loading ? <p className={styles.muted}>Loading calendars…</p> : null}
              <div
                className={styles.schedule}
                style={{ gridTemplateColumns: `76px repeat(${Math.max(visibleCalendars.length, 1)}, minmax(168px, 1fr))` }}
              >
                <div className={styles.corner} />
                {visibleCalendars.map((calendar) => (
                  <div className={styles.advisorHead} key={calendar.provider.id}>
                    <span className={styles.avatar}>{initials(calendar.provider.name)}</span>
                    <div>
                      <strong>{calendar.provider.name}</strong>
                      <small>{calendar.provider.visible ? 'On the booking page' : 'Hidden'}</small>
                    </div>
                  </div>
                ))}
                {BOARD_HOURS.map((hour) => (
                  <div className={styles.hourRow} key={hour} style={{ display: 'contents' }}>
                    <div className={styles.time}>{hourLabel(hour)}</div>
                    {visibleCalendars.map((calendar) => {
                      const events = eventsFor(calendar, hour);
                      const key = `${calendar.provider.id}-${hour}`;
                      return (
                        <div
                          className={`${styles.cell} ${dropKey === key ? styles.cellDrop : ''}`}
                          key={key}
                          onDragOver={(nativeEvent) => allowDrop(key, nativeEvent)}
                          onDragLeave={() => {
                            if (dropKey === key) setDropKey('');
                          }}
                          onDrop={(nativeEvent) => dropOnCell(calendar.provider.id, hour, nativeEvent)}
                        >
                          {events.map((event) => {
                            const isBooking = event.kind === 'booking';
                            return (
                              <button
                                key={event.id}
                                type="button"
                                draggable={isBooking && !working}
                                className={`${styles.event} ${styles[meetingKind(event)] || styles.busy} ${
                                  isBooking ? styles.draggable : ''
                                } ${dragId === event.id ? styles.eventDragging : ''}`}
                                onDragStart={(nativeEvent) => beginDrag(event, calendar.provider, nativeEvent)}
                                onDragEnd={endDrag}
                                onClick={() => handleEventClick(event, calendar.provider)}
                              >
                                <strong>
                                  {isBooking ? event.guest?.name || event.title : event.title}
                                </strong>
                                <span>
                                  {formatTime(event.startIso, timezone)} · {kindLabel(event)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </section>
          </>
        ) : null}

        {view === 'queue' ? (
          <section className={styles.panel}>
            {filteredBookings.length === 0 ? (
              <p className={styles.muted}>No calls match this week.</p>
            ) : (
              <ul className={styles.queue}>
                {filteredBookings.map((event) => (
                  <li key={`${event.provider.id}-${event.id}`}>
                    <div className={styles.queueTime}>
                      <strong>{formatTime(event.startIso, timezone)}</strong>
                      <span>{formatWhen(event.startIso, timezone).split(',')[0]}</span>
                    </div>
                    <div>
                      <strong>{event.guest?.name || 'Guest'}</strong>
                      <span>{event.guest?.email}</span>
                    </div>
                    <em className={styles[meetingKind(event)]}>{kindLabel(event)}</em>
                    <span>{event.provider.name}</span>
                    <button type="button" onClick={() => openBooking(event)}>
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {view === 'team' ? (
          <section className={styles.teamGrid}>
            {calendars.map((calendar) => {
              const count = bookings.filter((event) => event.provider.id === calendar.provider.id).length;
              return (
                <article key={calendar.provider.id} className={styles.teamCard}>
                  <span className={styles.avatarLg}>{initials(calendar.provider.name)}</span>
                  <h2>{calendar.provider.name}</h2>
                  <p>{calendar.provider.email}</p>
                  <strong>{count} calls this week</strong>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={Boolean(calendar.provider.visible)}
                      onChange={(event) => setAdvisorVisible(calendar.provider.id, event.target.checked)}
                    />
                    <span>{calendar.provider.visible ? 'Shown on the booking page' : 'Hidden from new bookings'}</span>
                  </label>
                </article>
              );
            })}
          </section>
        ) : null}

        {view === 'holds' ? (
          <div className={styles.holdLayout}>
            <form className={styles.panel} onSubmit={protectTime}>
              <h2>Protect overflow</h2>
              <p>A hold hides that time on the public page and leaves real meetings in place.</p>
              <label>
                Advisor
                <select
                  value={hold.providerId}
                  onChange={(event) => setHold((current) => ({ ...current, providerId: event.target.value }))}
                >
                  {calendars.map((calendar) => (
                    <option key={calendar.provider.id} value={calendar.provider.id}>
                      {calendar.provider.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className={styles.holdTimes}>
                <label>
                  Date
                  <input
                    type="date"
                    value={hold.date}
                    onChange={(event) => setHold((current) => ({ ...current, date: event.target.value }))}
                  />
                </label>
                <label>
                  Start
                  <input
                    type="time"
                    value={hold.start}
                    onChange={(event) => setHold((current) => ({ ...current, start: event.target.value }))}
                  />
                </label>
                <label>
                  End
                  <input
                    type="time"
                    value={hold.end}
                    onChange={(event) => setHold((current) => ({ ...current, end: event.target.value }))}
                  />
                </label>
              </div>
              <label>
                Note
                <input
                  value={hold.note}
                  onChange={(event) => setHold((current) => ({ ...current, note: event.target.value }))}
                  placeholder="Lunch, interview, overflow"
                />
              </label>
              <div className={styles.holdActions}>
                <button type="submit">Protect this time</button>
                <button type="button" className={styles.ghost} onClick={() => protectDay(hold.providerId, hold.date)}>
                  Close the full day
                </button>
              </div>
            </form>
            <section className={styles.panel}>
              <h2>Active holds</h2>
              {holds.length === 0 ? (
                <p className={styles.muted}>No overflow holds this week.</p>
              ) : (
                <ul className={styles.holdList}>
                  {holds
                    .slice()
                    .sort((a, b) => Date.parse(a.startIso) - Date.parse(b.startIso))
                    .map((event) => (
                      <li key={`${event.provider.id}-${event.id}`}>
                        <div>
                          <strong>{event.provider.name}</strong>
                          <span>
                            {formatWhen(event.startIso, timezone)} – {formatTime(event.endIso, timezone)}
                          </span>
                        </div>
                        <button type="button" onClick={() => removeHold(event.provider.id, event.id)}>
                          Release
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </main>

      {selected ? (
        <div className={styles.drawerWrap}>
          <button type="button" className={styles.backdrop} onClick={() => setSelected(null)} aria-label="Close" />
          <aside className={styles.drawer}>
            <button type="button" className={styles.close} onClick={() => setSelected(null)} aria-label="Close panel">
              <span className="material-symbols-outlined">close</span>
            </button>
            <span className={`${styles.badge} ${styles[meetingKind(selected)]}`}>{kindLabel(selected)}</span>
            <h2>{selected.guest?.name || 'Guest'}</h2>
            <p>{selected.guest?.email || 'No email on the calendar event'}</p>
            <p>{selected.guest?.phone || 'No phone on file'}</p>
            <dl className={styles.facts}>
              <div>
                <dt>When</dt>
                <dd>{formatWhen(selected.startIso, timezone)} ET</dd>
              </div>
              <div>
                <dt>Advisor</dt>
                <dd>{selected.provider.name}</dd>
              </div>
            </dl>
            {selected.meetLink ? (
              <a className={styles.meet} href={selected.meetLink} target="_blank" rel="noreferrer">
                Open Google Meet
              </a>
            ) : null}
            <label>
              Move to
              <select value={moveProviderId} onChange={(event) => setMoveProviderId(event.target.value)}>
                {calendars.map((calendar) => (
                  <option key={calendar.provider.id} value={calendar.provider.id}>
                    {calendar.provider.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date
              <input type="date" value={moveDate} onChange={(event) => setMoveDate(event.target.value)} />
            </label>
            <p className={styles.slotLabel}>Open times</p>
            <div className={styles.slots}>
              {slotsLoading ? <p className={styles.muted}>Checking the calendar…</p> : null}
              {!slotsLoading && slots.length === 0 ? (
                <p className={styles.muted}>No open top-of-hour times that day.</p>
              ) : null}
              {slots.map((slot) => (
                <button key={slot.start} type="button" disabled={working} onClick={() => runMove(slot.start)}>
                  {slot.label}
                </button>
              ))}
            </div>
            <button type="button" className={styles.danger} disabled={working} onClick={cancelSelected}>
              Cancel this call
            </button>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
